import { supabase } from './supabase'

/**
 * Convertit et redimensionne une image en WebP
 * @param {File} file - Le fichier image à convertir
 * @param {number} maxWidth - Largeur maximale en pixels (défaut: 1200)
 * @param {number} quality - Qualité de compression (0-1), défaut 0.85
 * @returns {Promise<Blob>} - Image convertie en WebP et redimensionnée
 */
export const convertToWebP = async (file, maxWidth = 1200, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        // Calculer les nouvelles dimensions en gardant le ratio
        let width = img.width
        let height = img.height

        // Si l'image est plus large que maxWidth, redimensionner
        if (width > maxWidth) {
          const ratio = maxWidth / width
          width = maxWidth
          height = Math.round(height * ratio)
          console.log(`Redimensionnement: ${img.width}x${img.height} → ${width}x${height}`)
        } else {
          console.log(`Pas de redimensionnement nécessaire: ${width}x${height}`)
        }

        // Créer un canvas avec les nouvelles dimensions
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')

        // Améliorer la qualité du redimensionnement
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height)

        // Convertir en WebP
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log(`Taille originale: ${(file.size / 1024).toFixed(2)} KB → Taille WebP: ${(blob.size / 1024).toFixed(2)} KB`)
              resolve(blob)
            } else {
              reject(new Error('Échec de la conversion en WebP'))
            }
          },
          'image/webp',
          quality
        )
      }

      img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'))
      img.src = e.target.result
    }

    reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'))
    reader.readAsDataURL(file)
  })
}

/**
 * Upload une image dans Supabase Storage (convertie automatiquement en WebP)
 * @param {File} file - Le fichier image à uploader
 * @param {string} folder - Dossier dans le bucket (ex: 'submissions', 'events')
 * @returns {Promise<{url: string, path: string}>} - URL publique et chemin de l'image
 */
export const uploadImage = async (file, folder = 'submissions') => {
  try {
    let fileToUpload = file

    // Si ce n'est pas déjà un WebP, convertir
    if (file.type !== 'image/webp') {
      console.log(`Conversion de ${file.type} vers WebP...`)
      const webpBlob = await convertToWebP(file)
      fileToUpload = new File([webpBlob], file.name.replace(/\.[^.]+$/, '.webp'), {
        type: 'image/webp'
      })
    }

    // Générer un nom de fichier unique avec timestamp
    const timestamp = Date.now()
    const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.webp`
    const filePath = `${folder}/${fileName}`

    // Upload vers Supabase Storage
    const { data, error } = await supabase.storage
      .from('event-images')
      .upload(filePath, fileToUpload, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) throw error

    // Récupérer l'URL publique
    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(filePath)

    return {
      url: publicUrl,
      path: filePath
    }
  } catch (error) {
    console.error('Erreur lors de l\'upload:', error.message)
    throw error
  }
}

/**
 * Supprime une image du Storage (réservé aux admins)
 * @param {string} imagePath - Chemin de l'image dans le bucket
 * @returns {Promise<void>}
 */
export const deleteImage = async (imagePath) => {
  try {
    const { error } = await supabase.storage
      .from('event-images')
      .remove([imagePath])

    if (error) throw error
  } catch (error) {
    console.error('Erreur lors de la suppression:', error.message)
    throw error
  }
}

/**
 * Valide qu'un fichier est une image valide
 * @param {File} file - Le fichier à valider
 * @returns {boolean}
 */
export const validateImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  const maxSize = 10 * 1024 * 1024 // 10 MB (avant optimisation)

  if (!validTypes.includes(file.type)) {
    throw new Error('Format de fichier non supporté. Utilisez JPG, PNG ou WebP.')
  }

  if (file.size > maxSize) {
    throw new Error('Le fichier est trop volumineux. Maximum 10 MB.')
  }

  return true
}

/**
 * Déplace une image de submissions vers events (quand une soumission est approuvée)
 * @param {string} sourcePath - Chemin actuel de l'image
 * @returns {Promise<{url: string, path: string}>}
 */
export const moveImageToEvents = async (sourcePath) => {
  try {
    // Télécharger l'image depuis submissions
    const { data: fileData, error: downloadError } = await supabase.storage
      .from('event-images')
      .download(sourcePath)

    if (downloadError) throw downloadError

    // Créer un nouveau nom dans le dossier events
    const fileName = sourcePath.split('/').pop()
    const newPath = `events/${fileName}`

    // Upload dans le nouveau dossier
    const { error: uploadError } = await supabase.storage
      .from('event-images')
      .upload(newPath, fileData, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) throw uploadError

    // Supprimer l'ancienne image
    await deleteImage(sourcePath)

    // Récupérer la nouvelle URL publique
    const { data: { publicUrl } } = supabase.storage
      .from('event-images')
      .getPublicUrl(newPath)

    return {
      url: publicUrl,
      path: newPath
    }
  } catch (error) {
    console.error('Erreur lors du déplacement:', error.message)
    throw error
  }
}
